// Profit On Your Terms's home, /poyt (POYT.dc.html's "Paid in full" Tweak at 1440; no board draws it
// on a phone or in the widths between, so there it takes PhoneWeek.dc.html's and PhoneLibrary.dc.html's
// pieces: 16px gutters, cards with 14px corners, the board's order stacked, buttons the whole width).
// src/products/poyt/home.js works out what it says. The hero sits under the menu, the full width of
// the page, as the board draws it; the sections under it follow the product's published home layout
// (layout.js POYT_HOME), with its default where none is published:
//
//   news       Adam's announcement for Profit On Your Terms, while he has 1 up
//   start      Start here: Deal analysis and Marketing setup, "1 of 2 done", and the footnote
//              (fixed: it moves, never hides)
//   tool       the two-offer analyser, opened in the hub's tool runner in a new tab
//   help       "Questions between sessions"
//   weeks      "Your 6 weeks" (fixed)
//   res        Resources: the 5 tabs, each locked card in its own tab, the toolkit yours for life
//   guarantee  the scripts guarantee
//   surgery    "Your deal surgery", only for an intake with it switched on
//
// The product route draws the frame, the payment banner, the read-only line and the welcome
// checklist above this. A secured place never reaches it: the place secured page shows instead
// (SecuredPage.jsx, stage 2). While staff view as a member nothing here writes: Book your 30
// minutes and the analyser are off, and Add to calendar and the links still work.
// Once any of Profit On Your Terms's recordings has Adam's own thumbnail (decision 47, 12 Oct), each of
// the 6 weeks starts with its recording's 16:9 frame: the thumbnail, or the grey panel with the week's
// date (product/recordingThumbs.js), all signed in 1 call. Until then the weeks are the board's.
import { useEffect, useState } from 'react'
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { Link } from '../../lib/router.jsx'
import { downloadIcsAll } from '../../lib/calendar.js'
import { useViewingAs, readOnlyControl, VIEWING_AS_LINE } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { useLayout, LayoutSections } from '../../product/Layout.jsx'
import { POYT_HOME } from '../../product/layout.js'
import { ResourcesSection } from '../../product/Resources.jsx'
import { ProductAnnouncement } from '../../product/ProductAnnouncement.jsx'
import { productAnnouncement } from '../../product/model.js'
import { resourceTabs } from '../../product/release.js'
import { LockLine, Tick, VideoIcon } from '../../product/Icons.jsx'
import {
  cardWords, heroFor, splitEmail, startHere, surgeryIntake, toolCardFor, weekRows,
} from '../../products/poyt/home.js'
import { askForSurgery, loadSurgeryRequest } from '../../products/poyt/data.js'
import { anyThumbnail, weekThumb } from '../../product/recordingThumbs.js'
import { RecordingThumb, useRecordingThumbs } from '../../product/RecordingThumb.jsx'
import '../../styles/poyt.css'

// POYT.dc.html: Start here beside the tool and questions cards; the guarantee beside the surgery
const TOP_COLUMNS = 'minmax(0, 1.6fr) minmax(0, 1fr)'
const PAIR_COLUMNS = 'repeat(2, minmax(0, 1fr))'
export const SEND_FAILED = "That didn't send. Check your connection and try again."
export const SURGERY_CLOSED = "The deal surgery isn't open for your intake."

export default function PoytHome({ product, owned }) {
  const { data } = useProductData()
  const { me, lib } = useMember()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const sections = useLayout(product, 'home', POYT_HOME)

  const hero = heroFor(data, product, nowMs)
  const sh = startHere(data, product)
  const tool = toolCardFor(data, product)
  const words = cardWords(product)
  const weeks = weekRows(data, product, nowMs)
  const surgery = words.surgery && surgeryIntake(data, owned) ? words.surgery : null
  const announcement = productAnnouncement(lib.data, product.id, nowMs)
  const hasResources = resourceTabs(data, nowMs).length > 0
  // each week's recording frame, once any recording has a thumbnail, signed together
  const any = anyThumbnail(data.recordings)
  const frames = any ? Object.fromEntries(weeks.rows.map(r => [r.id, weekThumb(data, r.id, any)])) : null
  const thumbs = useRecordingThumbs(frames ? Object.values(frames) : [])

  const parts = {
    news: { place: 'full', when: Boolean(announcement), render: () => <ProductAnnouncement announcement={announcement} /> },
    start: { place: 'main', columns: TOP_COLUMNS, when: Boolean(sh), render: () => <StartHere sh={sh} /> },
    tool: { place: 'side', columns: TOP_COLUMNS, when: Boolean(tool), render: () => <ToolCard tool={tool} viewingAs={viewingAs} /> },
    help: { place: 'side', columns: TOP_COLUMNS, when: Boolean(words.help), render: () => <Help help={words.help} email={words.email} /> },
    weeks: { place: 'full', when: weeks.rows.length > 0, render: () => <Weeks weeks={weeks} frames={frames} thumbs={thumbs} /> },
    res: { place: 'full', when: hasResources, render: () => <ResourcesSection product={product} /> },
    guarantee: { place: 'main', columns: PAIR_COLUMNS, when: Boolean(words.guarantee), render: () => <Guarantee g={words.guarantee} email={words.email} /> },
    surgery: {
      place: 'side', columns: PAIR_COLUMNS, when: Boolean(surgery),
      render: () => <Surgery words={surgery} personId={me.person.id} productId={product.id} viewingAs={viewingAs} />,
    },
  }

  return (
    <>
      <Hero hero={hero} productName={product.name} />
      <div className="py-home">
        {sections
          ? <LayoutSections sections={sections} parts={parts} className="py-home-layout" />
          : <div className="py-home--loading" aria-busy="true" />}
      </div>
    </>
  )
}

// The hero (POYT.dc.html): the intake and when it starts, or the next live session, with the
// countdown, Join on Zoom on the night and Add to calendar. It isn't a layout section.
function Hero({ hero, productName }) {
  const [added, setAdded] = useState(false)
  const card = hero.card
  return (
    <section className="pp-hero py-hero" aria-labelledby="py-hero-h1">
      <div className="pp-hero-main">
        <div className="pp-hero-eyebrow">{hero.eyebrow}</div>
        <h1 className="pp-hero-h1" id="py-hero-h1">
          {hero.title.lead}{hero.title.mark && <span className="pp-underline">{hero.title.mark}</span>}
        </h1>
        {hero.line && <p className="pp-hero-line">{hero.line}</p>}
      </div>
      {card && (
        <div className="pp-hero-card">
          <div className="pp-count"><span className="pp-count-big">{card.big}</span><span className="pp-count-label">{card.label}</span></div>
          {card.join && (
            <a className="py-join" href={card.join.href} target="_blank" rel="noopener noreferrer" aria-label={card.join.aria}>
              <VideoIcon size={18} />{card.join.label}
            </a>
          )}
          {card.calendar && (
            <button type="button" className="pp-cal" onClick={() => { downloadIcsAll({ name: productName, events: card.calendar.events }); setAdded(true) }}>
              {added ? 'Added to your calendar' : card.calendar.label}
            </button>
          )}
          {card.note && <span className="pp-hero-note">{card.note}</span>}
        </div>
      )}
    </section>
  )
}

// Start here (POYT.dc.html): the 2 rows of screens, where they are in each, and the footnote. Its heading names
// it as the pre-course training (Adam, 14 Oct: "at the moment it's easy to ignore")
function StartHere({ sh }) {
  return (
    <section className="py-card py-start" aria-labelledby="py-start-h2">
      <div className="py-card-head">
        <h2 className="py-h2" id="py-start-h2">Start here: pre-course training</h2>
        <span className="py-count">{sh.count}</span>
      </div>
      {sh.rows.map(r => (
        <div key={r.id} className={`py-step py-step--${r.state}`}>
          <span className={r.done ? 'py-disc py-disc--done' : 'py-disc'} aria-hidden="true">{r.done ? <Tick size={18} stroke={3} /> : r.num}</span>
          <div className="py-step-text">
            <h3 className="py-step-title">{r.title}{r.done && <span className="sr-only">, done</span>}</h3>
            {r.body && <p className="py-step-body">{r.body}</p>}
          </div>
          {r.button
            ? (
              <Link to={r.button.href} className={r.done ? 'py-btn py-btn--line py-step-btn' : 'py-btn py-step-btn'}
                aria-label={`${r.button.label}: ${r.title}`}>{r.button.label}</Link>
            )
            : <span className="py-step-wait"><LockLine size={15} />{r.waitLine}</span>}
        </div>
      ))}
      {sh.footnote && <p className="py-start-foot">{sh.footnote}</p>}
    </section>
  )
}

// The tool card: the two-offer analyser, its page in the hub in a new tab (the tool runner)
function ToolCard({ tool, viewingAs }) {
  return (
    <section className="py-tool" aria-labelledby="py-tool-title">
      <span className="py-tool-eyebrow">{tool.eyebrow}</span>
      <h2 className="py-tool-title" id="py-tool-title">{tool.title}</h2>
      {tool.line && <p className="py-tool-line">{tool.line}</p>}
      {viewingAs
        ? <button type="button" className="py-tool-btn" {...readOnlyControl(true)}>{tool.button}</button>
        : <a className="py-tool-btn" href={tool.href} target="_blank" rel="noopener">{tool.button}</a>}
    </section>
  )
}

// A line with the questions address as a link
function EmailLine({ text, email }) {
  const [before, address, after] = splitEmail(text, email)
  if (!address) return text
  return <>{before}<a className="py-mail" href={`mailto:${address}`}>{address}</a>{after}</>
}

// "Questions between sessions"
function Help({ help, email }) {
  return (
    <section className="py-card py-card--pad py-help" aria-labelledby="py-help-title">
      <h2 className="py-h3" id="py-help-title">{help.title}</h2>
      <p className="py-text"><EmailLine text={help.line} email={email} /></p>
    </section>
  )
}

// "Your 6 weeks": a week that's open links to its page; each with its recording's frame once any
// recording has a thumbnail
function Weeks({ weeks, frames, thumbs }) {
  return (
    <section className="py-card py-weeks" aria-labelledby="py-weeks-h2">
      <div className="py-card-head">
        <h2 className="py-h2" id="py-weeks-h2">{weeks.heading}</h2>
        <span className="py-head-note">{weeks.note}</span>
      </div>
      <ol className={frames ? 'py-wk-list py-wk-list--thumbs' : 'py-wk-list'}>
        {weeks.rows.map(w => (
          <li key={w.id} className={`py-wk py-wk--${w.tone}${w.current ? ' py-wk--current' : ''}${w.href ? ' py-wk--open' : ''}`}>
            {frames && <RecordingThumb card={frames[w.id]} thumbs={thumbs} className="py-wk-thumb" />}
            <span className="py-wk-label">{w.label}</span>
            <span className="py-wk-date">{w.date}</span>
            <span className="py-wk-main">
              {w.href
                ? <Link to={w.href} className="py-wk-title py-wk-link" aria-label={`${w.label}: ${w.title}`}>{w.title}</Link>
                : <span className="py-wk-title">{w.title}</span>}
              {w.pack && <span className="py-wk-pack">{w.pack}</span>}
            </span>
            <span className="py-wk-status">{w.done && <Tick size={15} />}{w.status}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

// The scripts guarantee
function Guarantee({ g, email }) {
  return (
    <section className="py-card py-card--pad py-guarantee" aria-labelledby="py-guarantee-title">
      <h2 className="py-h3 py-h3--big" id="py-guarantee-title">{g.title}</h2>
      <p className="py-text">{g.body}</p>
      {g.link && <a className="py-guarantee-link" href={`mailto:${email}`}>{g.link}</a>}
    </section>
  )
}

// "Your deal surgery": Book your 30 minutes puts a request on Adam's Today (no email), then it reads
// "Adam will send you a time"
function Surgery({ words, personId, productId, viewingAs }) {
  const [s, set] = useState({ status: 'loading', asked: null, line: null })
  useEffect(() => {
    let live = true
    loadSurgeryRequest(personId, productId)
      .then(row => { if (live) set({ status: 'ready', asked: row, line: null }) })
      // if it can't be read, the button still works: asking again is answered as asked already
      .catch(() => { if (live) set({ status: 'ready', asked: null, line: null }) })
    return () => { live = false }
  }, [personId, productId])

  function book() {
    if (viewingAs || s.status !== 'ready') return
    set({ ...s, status: 'busy', line: null })
    askForSurgery(personId, productId)
      .then(row => set({ status: 'ready', asked: row || { id: 'asked' }, line: null }))
      .catch(e => {
        if (e?.code === '23505') return set({ status: 'ready', asked: { id: 'asked' }, line: null })
        const closed = e?.code === '42501' && /no_deal_surgery/.test(e?.message || '')
        set({ status: 'ready', asked: null, line: closed ? SURGERY_CLOSED : viewingAs ? VIEWING_AS_LINE : SEND_FAILED })
      })
  }

  return (
    <section className="py-card py-card--pad py-surgery" aria-labelledby="py-surgery-title">
      <h2 className="py-h3 py-h3--big" id="py-surgery-title">{words.title}</h2>
      <p className="py-text">{words.body}</p>
      {s.asked
        ? <span className="py-btn py-btn--line py-btn--quiet py-surgery-btn" role="status">{words.done}</span>
        : (
          <button type="button" className="py-btn py-btn--line py-btn--quiet py-surgery-btn" onClick={book}
            aria-busy={s.status !== 'ready' || undefined} {...readOnlyControl(viewingAs)}>{words.button}</button>
        )}
      {s.line && <p className="py-msg" role="alert">{s.line}</p>}
    </section>
  )
}
